import { useLiveQuery } from 'dexie-react-hooks';
import type { Estimate, Farm, MasterData } from '@fruitways/shared';
import { openEstimateWeek, previousEstimateWeek, toIsoDate } from '@fruitways/shared';
import { KV, db } from '@/db/database';

/** One kv row as live state. Undefined while loading and when the key has never been written. */
export function useKv<T>(key: string): T | undefined {
  return useLiveQuery(async () => (await db.kv.get(key))?.value as T | undefined, [key]);
}

export const useMasterData = () => useKv<MasterData>(KV.masterData);

export function useEstimates(): Estimate[] | undefined {
  return useLiveQuery(() => db.estimates.toArray(), []);
}

export function useEstimate(id: string | undefined): Estimate | undefined | null {
  return useLiveQuery(async () => (id ? ((await db.estimates.get(id)) ?? null) : null), [id]);
}

export interface FarmGroup {
  farm: Farm;
  rows: Estimate[];
}

/** Estimates grouped by farm in farm order, newest week first, optionally capped per farm. */
export function groupByFarm(estimates: Estimate[], farms: Farm[], limit = 0): FarmGroup[] {
  return farms
    .map((farm) => {
      let rows = estimates
        .filter((e) => e.farmId === farm.id)
        .sort((a, b) => (a.weekStart < b.weekStart ? 1 : -1));
      if (limit) rows = rows.slice(0, limit);
      return { farm, rows };
    })
    .filter((g) => g.rows.length > 0);
}

/** The season whose date range contains today, else the newest active one. */
export function seasonForToday(master: MasterData | undefined, today = toIsoDate(new Date())) {
  if (!master) return undefined;
  return (
    master.seasons.find((s) => s.startDate <= today && today <= s.endDate) ?? master.seasons[0]
  );
}

/** The estimate week a grower may capture or edit right now (Thursday to Wednesday). */
export function openWeek(today = toIsoDate(new Date())) {
  return openEstimateWeek(today);
}

/** The estimate week before the open one. */
export function previousWeek(today = toIsoDate(new Date())) {
  return previousEstimateWeek(today);
}

/**
 * The varieties a grower may capture for a week: those the admin has marked in harvest for it.
 *
 * A week with none falls back to every variety. Harvest runs January to May, so most of the year
 * no week has any, and a strict filter would leave growers unable to capture at all.
 */
export function varietiesForWeek(
  master: MasterData | undefined,
  seasonId: string | undefined,
  weekNumber: number,
): string[] {
  const all = (master?.varieties ?? []).map((v) => v.code);
  if (!seasonId) return all;
  const inHarvest = (master?.harvestWeeks ?? [])
    .filter((h) => h.seasonId === seasonId && h.weekNumber === weekNumber)
    .map((h) => h.varietyCode);
  if (inHarvest.length === 0) return all;
  // Keep the master data's order rather than the harvest weeks' insertion order.
  const wanted = new Set(inHarvest);
  return all.filter((code) => wanted.has(code));
}

/**
 * The only estimate weeks a grower sees: the open one and the one before it. Anything older is
 * past its cut-off and cannot be acted on, so it is never shown.
 */
export function visibleWeekStarts(today = toIsoDate(new Date())): string[] {
  return [openEstimateWeek(today).weekStart, previousEstimateWeek(today).weekStart];
}

/**
 * True when every farm the grower is assigned has an estimate in for the given week. Submitting
 * closes only then: with one farm still outstanding the button must stay live for it.
 */
export function allFarmsSubmitted(
  farms: ReadonlyArray<Pick<Farm, 'id'>>,
  estimates: ReadonlyArray<Pick<Estimate, 'farmId' | 'weekStart'>>,
  weekStart: string,
): boolean {
  return (
    farms.length > 0 &&
    farms.every((f) => estimates.some((e) => e.farmId === f.id && e.weekStart === weekStart))
  );
}
